import { Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  getDashboardAffiliateWorkload,
  getDashboardAffiliates,
} from "../../repository/data.repository";
import {
  DashboardAffiliateWorkload,
  DashboardAffiliates,
} from "../../repository/data.default";
import {
  ALARM_RED,
  ChartColorScale,
  WARN_GOLD,
  renderDualTrend,
  renderMetricChart,
  renderStackedColumn,
} from "./MetricChartRender";
import { InsightChartCard, InsightStrip } from "./InsightCards";
import { pickBucket } from "./AnalyticsRange";
import { DashboardSectionScope } from "./DashboardSection";
import { useServerPagedTable } from "./useServerPagedTable";

/** Page size of the per-affiliate workload table. */
const WORKLOAD_PAGE_SIZE = 8;

// Counselling request statuses on the open backlog: red = raised and still unactioned (needs a
// therapist/slot), gold = a session has been proposed and awaits the user.
const REQUEST_STATUS_COLORS: ChartColorScale = {
  domain: ["Requested", "Proposed"],
  range: [ALARM_RED, WARN_GOLD],
};

// The waiting-time bands, pinned to their ordinal order wherever they are an axis.
const AGE_BAND_ORDER = ["0–3", "4–7", "8–14", "15+"];

// The submit→appraise turnaround bands, likewise pinned to their ordinal order.
const REVIEW_BAND_ORDER = ["0–2", "3–7", "8–14", "15+"];

// Affiliate operations from one server-side rollup: the open counselling backlog by waiting time,
// demand vs delivered sessions, report appraisal turnaround, the trainings pipeline and which
// affiliates carry the load. Backlog figures are point-in-time snapshots; the trends and the
// turnaround honour the date range.
function AffiliatesCard(props: DashboardSectionScope) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [affiliates, setAffiliates] = useState<DashboardAffiliates | undefined>();

  const from = props.range?.[0];
  const to = props.range?.[1];
  const bucket = pickBucket(from, to);

  // The per-affiliate workload table pages on the server; its state lives beside the rollup's.
  // Unlike the backlog stats it honours the date range (sessions/trainings within the period).
  const { rows, totalRecords, tableLoading, pagination } =
    useServerPagedTable<DashboardAffiliateWorkload>(
      (page) =>
        getDashboardAffiliateWorkload({
          companyUid: props.companyUid,
          from,
          to,
          pageNumber: page,
          pageSize: WORKLOAD_PAGE_SIZE,
        }),
      [props.companyUid, from, to],
      WORKLOAD_PAGE_SIZE,
    );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getDashboardAffiliates({
      companyUid: props.companyUid,
      from,
      to,
      bucket,
    })
      .then((data) => {
        if (cancelled) return;
        setAffiliates(data);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [props.companyUid, from, to, bucket]);

  const unassigned = affiliates?.unassignedOpenRequests ?? 0;
  const awaitingAppraisal = affiliates?.reportsAwaitingAppraisal ?? 0;
  // Rates round to one decimal before interpolation.
  const completionRate = Math.round((affiliates?.completionRate ?? 0) * 10) / 10;
  const attendanceRate =
    affiliates?.attendanceRate != null
      ? Math.round(affiliates.attendanceRate * 10) / 10
      : null;
  const trainingsCancelled = affiliates?.trainingsCancelled ?? 0;

  // Band order pinned to the waiting-time scale wherever the age bands are an axis.
  const bandIndex = (b: string) => {
    const i = AGE_BAND_ORDER.indexOf(b);
    return i < 0 ? AGE_BAND_ORDER.length : i;
  };
  const ageBands = [...(affiliates?.ageBands ?? [])]
    .sort((a, b) => bandIndex(a.band) - bandIndex(b.band))
    .map((b) => ({ type: b.band, series: b.status, value: b.count }));
  const demand = (affiliates?.demandVsDelivery ?? []).flatMap((p) => [
    { date: dayjs(p.date).format("YYYY-MM-DD"), type: "Raised", value: p.raised },
    { date: dayjs(p.date).format("YYYY-MM-DD"), type: "Held", value: p.held },
  ]);
  // Turnaround band order pinned like the age bands (the plain column renders in data order).
  const reviewBandIndex = (b: string) => {
    const i = REVIEW_BAND_ORDER.indexOf(b);
    return i < 0 ? REVIEW_BAND_ORDER.length : i;
  };
  const turnaround = [...(affiliates?.reviewTurnaround ?? [])]
    .sort((a, b) => reviewBandIndex(a.band) - reviewBandIndex(b.band))
    .map((b) => ({
      type: b.band,
      value: b.count,
    }));
  const trainings = (affiliates?.trainingsTrend ?? []).flatMap((p) => [
    { date: dayjs(p.date).format("YYYY-MM-DD"), type: "Planned", value: p.planned },
    { date: dayjs(p.date).format("YYYY-MM-DD"), type: "Held", value: p.held },
  ]);

  const workloadColumns: ColumnsType<DashboardAffiliateWorkload> = [
    { title: "Affiliate", dataIndex: "userName" },
    { title: "Open requests", dataIndex: "openRequests", align: "right" },
    { title: "Sessions held", dataIndex: "sessionsHeld", align: "right" },
    { title: "Trainings planned", dataIndex: "trainingsPlanned", align: "right" },
    { title: "Trainings held", dataIndex: "trainingsHeld", align: "right" },
    {
      title: "Attendance",
      dataIndex: "attendanceRate",
      align: "right",
      render: (rate: number | null) =>
        rate != null ? (
          `${rate}%`
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ];

  const activeBucket = affiliates?.bucket ?? bucket;

  return (
    <div className="flex w-full flex-col gap-3">
      <InsightStrip
        groups={[
          {
            label: "Counselling",
            items: [
              `${(affiliates?.openRequests ?? 0).toLocaleString()} open requests`,
              unassigned > 0 ? (
                <Tooltip title="Open counselling requests with no therapist assigned yet">
                  <Typography.Text type="warning">
                    {unassigned.toLocaleString()} unassigned
                  </Typography.Text>
                </Tooltip>
              ) : (
                "0 unassigned"
              ),
              `${affiliates?.avgOpenAgeDays ?? 0}d avg open age`,
              `${completionRate}% completion`,
            ],
          },
          {
            label: "Reports",
            items: [
              awaitingAppraisal > 0 ? (
                <Typography.Text type="warning">
                  {awaitingAppraisal.toLocaleString()} awaiting appraisal
                </Typography.Text>
              ) : (
                "0 awaiting appraisal"
              ),
            ],
          },
          {
            label: "Trainings",
            items: [
              `${(affiliates?.upcomingTrainings ?? 0).toLocaleString()} upcoming`,
              `${(affiliates?.trainingsHeld ?? 0).toLocaleString()} held`,
              // Informational, never an alarm — a cancellation is a fact of delivery, not a fault.
              <Tooltip title="Cancelled within the period (approximated by last update)">
                <span>{trainingsCancelled.toLocaleString()} cancelled</span>
              </Tooltip>,
              attendanceRate != null ? (
                `${attendanceRate}% attendance`
              ) : (
                <Typography.Text type="secondary">attendance not marked</Typography.Text>
              ),
            ],
          },
        ]}
      />
      <Typography.Text type="secondary" className="text-xs">
        The open backlog and its age bands are point-in-time snapshots — the date range
        applies to the demand, turnaround and trainings charts and the workload table.
      </Typography.Text>
      <div className="flex flex-row flex-wrap gap-3">
        <InsightChartCard
          title="Open requests by age"
          subtitle="Open counselling requests by days waiting"
          loading={loading}
          error={error}
        >
          {renderStackedColumn(ageBands, "Open requests", REQUEST_STATUS_COLORS, 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Demand vs delivery"
          subtitle={`By ${activeBucket} · requests raised vs sessions held`}
          loading={loading}
          error={error}
        >
          {renderDualTrend(demand, "Requests", 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Report review turnaround"
          subtitle="Submit → appraise time of appraisals in the period, days"
          loading={loading}
          error={error}
        >
          {renderMetricChart("column", {}, turnaround, "Appraisals", 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Trainings planned vs held"
          subtitle={`By ${activeBucket} · trainings planned (created) vs held (completed)`}
          loading={loading}
          error={error}
        >
          {renderDualTrend(trainings, "Trainings", 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Affiliate workload"
          subtitle="Every affiliate, busiest first — idle rows are the capacity headroom."
          loading={false}
          error={error}
        >
          {unassigned > 0 && (
            <div>
              <Tag color="red">
                {unassigned.toLocaleString()} open requests have no therapist assigned
              </Tag>
            </div>
          )}
          <Table
            size="small"
            rowKey="userUid"
            columns={workloadColumns}
            dataSource={rows}
            loading={tableLoading}
            pagination={pagination}
          />
          <Typography.Text type="secondary" className="text-xs">
            {totalRecords.toLocaleString()} affiliates · trainings count their lead
            trainer — co-trained sessions are not double-counted.
          </Typography.Text>
        </InsightChartCard>
      </div>
    </div>
  );
}

export default AffiliatesCard;
